import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFormik } from 'formik';
import { useIntl } from 'react-intl';
import { boolean, object, string } from 'yup';

import isEmpty from 'lodash/isEmpty';

import Fade from '@mui/material/Fade';
import Grid from '@mui/material/Grid2';
import Slide from '@mui/material/Slide';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';

import {
  useDeleteQuestionOption,
  useCreateQuestionOption,
  useUpdateQuestionOption,
} from 'src/pages/TutorialDetailsPage/api';
import Dropdown from 'src/components/Dropdown';
import DataTable from 'src/components/DataTable';
import TextInput from 'src/components/TextInput';
import { PencilAdornment } from 'src/utils/components';
import ConfirmationDialog from 'src/components/ConfirmationDialog';

import messages from './messages';

type TutorialOptionFormProps = {
  tutorialQuestion: UCM.TutorialQuestionType;
};

export default function TutorialOptionForm({
  tutorialQuestion,
}: TutorialOptionFormProps) {
  const { formatMessage } = useIntl();

  const [validateOnChange, setValidateOnChange] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [selectedOption, setSelectedOption] =
    useState<UCM.QuestionOptionType | null>(null);

  const OptionFormRef = useRef<HTMLDivElement>(null);
  const addOptionRef = useRef<HTMLDivElement>(null);

  const { mutate: deleteQuestionOption } = useDeleteQuestionOption();
  const { mutate: createQuestionOption, isPending: isCreating } =
    useCreateQuestionOption();
  const { mutate: updateQuestionOption, isPending: isUpdating } =
    useUpdateQuestionOption();

  const formatBooleanField = useCallback((value: boolean) => {
    return value
      ? formatMessage(messages.yesLabel)
      : formatMessage(messages.noLabel);
  }, []);

  const booleanOptions = useMemo(
    () => [
      { value: true, label: formatMessage(messages.yesLabel) },
      { value: false, label: formatMessage(messages.noLabel) },
    ],
    [],
  );

  const initialValues = {
    code: '',
    textEn: '',
    textDe: '',
    isVisible: false,
    isCorrect: false,
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validateOnChange: validateOnChange,
    validationSchema: object().shape({
      code: string().required(),
      textEn: string().required(),
      textDe: string().required(),
      isVisible: boolean().required(),
      isCorrect: boolean().required(),
    }),
    onSubmit: () => {},
  });

  const { values, errors } = formik;

  useEffect(() => {
    if (selectedOption && showForm) {
      formik.setValues({
        code: selectedOption.code,
        textEn: selectedOption.text.en,
        textDe: selectedOption.text.de,
        isVisible: selectedOption.isVisible,
        isCorrect: selectedOption.isCorrect,
      });
    }
  }, [selectedOption, showForm]);

  useEffect(() => {
    if (showForm) hideDiv(addOptionRef.current);
    else hideDiv(OptionFormRef.current);
  }, [showForm]);

  const handleOptionAdd = useCallback(() => {
    setShowForm(true);
  }, []);

  const handleEdit = useCallback((option: UCM.QuestionOptionType) => {
    setSelectedOption(option);
    setShowForm(true);
  }, []);

  const columns: Array<{
    id: keyof UCM.QuestionOptionType | string;
    label: string | React.ReactNode;
  }> = useMemo(() => {
    return [
      {
        id: 'code',
        label: formatMessage(messages.codeColumnLabel),
        render: (code: string) => (
          <Typography variant="inherit">{code}</Typography>
        ),
      },
      {
        id: 'textEn',
        label: formatMessage(messages.textEnColumnLabel),
        render: (_: Object, option: UCM.QuestionOptionType) => (
          <Typography variant="inherit">{option.text.en}</Typography>
        ),
      },
      {
        id: 'textDe',
        label: formatMessage(messages.textDeColumnLabel),
        render: (_: Object, option: UCM.QuestionOptionType) => (
          <Typography variant="inherit">{option.text.de}</Typography>
        ),
      },
      {
        id: 'isVisible',
        align: 'center',
        label: formatMessage(messages.visibleColumnLabel),
        render: (isVisible: boolean) => (
          <Typography variant="inherit">
            {formatBooleanField(isVisible)}
          </Typography>
        ),
      },
      {
        id: 'isCorrect',
        align: 'center',
        label: formatMessage(messages.correctColumnLabel),
        render: (isCorrect: boolean) => (
          <Typography variant="inherit">
            {formatBooleanField(isCorrect)}
          </Typography>
        ),
      },
      {
        id: 'actions',
        label: (
          <Fade in={!showForm} mountOnEnter unmountOnExit>
            <Grid
              size={12}
              container
              ref={addOptionRef}
              alignItems="center"
              justifyContent="flex-end"
            >
              <Button onClick={handleOptionAdd}>
                {formatMessage(messages.addButton)}
              </Button>
            </Grid>
          </Fade>
        ),
        align: 'center',
        width: 0,
        render: (_: Object, option: UCM.QuestionOptionType) => (
          <Grid display="flex" alignItems="center">
            <Tooltip arrow title={formatMessage(messages.editTooltip)}>
              <IconButton onClick={() => handleEdit(option)}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <ConfirmationDialog
              onConfirm={() => handleDelete(option.uuid)}
              message={formatMessage(messages.deleteOptionConfirmMessage)}
            >
              {(handleOpen) => (
                <Tooltip arrow title={formatMessage(messages.deleteTooltip)}>
                  <IconButton onClick={handleOpen}>
                    <DeleteForeverOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </ConfirmationDialog>
          </Grid>
        ),
      },
    ];
  }, [showForm]);

  const handleClose = useCallback(() => {
    setValidateOnChange(false);
    formik.resetForm();
    setShowForm(false);
    setSelectedOption(null);
  }, []);

  const handleDelete = useCallback(
    (uuid: string) => {
      deleteQuestionOption({
        tutorialUuid: tutorialQuestion.tutorialUuid,
        questionUuid: tutorialQuestion.uuid,
        optionUuid: uuid,
      });
    },
    [tutorialQuestion.question, tutorialQuestion.tutorialUuid],
  );

  const handleSave = useCallback(async () => {
    setValidateOnChange(true);

    const errors = await formik.validateForm();
    const isValid = isEmpty(errors);

    if (!isValid) return;

    const updatedValues = {
      ...values,
      questionUuid: tutorialQuestion.uuid,
      tutorialUuid: tutorialQuestion.tutorialUuid,
    };

    if (!selectedOption) {
      createQuestionOption(updatedValues, { onSuccess: handleClose });
    } else {
      updateQuestionOption(
        { ...updatedValues, optionUuid: selectedOption.uuid },
        { onSuccess: handleClose },
      );
    }
  }, [values, selectedOption]);

  return (
    <Grid container size={12} spacing={2}>
      <Slide in={showForm} mountOnEnter unmountOnExit direction="left">
        <Grid
          p={2}
          m={1}
          container
          size={12}
          component={Paper}
          flexWrap="nowrap"
          ref={OptionFormRef}
          alignItems="center"
        >
          <Grid container size={12} spacing={2}>
            <Grid size={{ xs: 12, md: 2.4 }}>
              <TextInput
                fullWidth
                name="code"
                value={values.code}
                error={!!errors.code}
                helperText={errors.code}
                EndIcon={PencilAdornment}
                onChange={formik.handleChange}
                label={formatMessage(messages.codeColumnLabel)}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2.4 }}>
              <TextInput
                fullWidth
                name="textEn"
                value={values.textEn}
                error={!!errors.textEn}
                helperText={errors.textEn}
                EndIcon={PencilAdornment}
                onChange={formik.handleChange}
                label={formatMessage(messages.textEnColumnLabel)}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2.4 }}>
              <TextInput
                fullWidth
                name="textDe"
                value={values.textDe}
                error={!!errors.textDe}
                helperText={errors.textDe}
                EndIcon={PencilAdornment}
                onChange={formik.handleChange}
                label={formatMessage(messages.textDeColumnLabel)}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2.4 }}>
              <Dropdown
                fullWidth
                name="isCorrect"
                options={booleanOptions}
                value={values.isCorrect}
                error={!!errors.isCorrect}
                helperText={errors.isCorrect}
                onChange={formik.handleChange}
                label={formatMessage(messages.correctColumnLabel)}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2.4 }}>
              <Dropdown
                fullWidth
                name="isVisible"
                options={booleanOptions}
                value={values.isVisible}
                error={!!errors.isVisible}
                helperText={errors.isVisible}
                onChange={formik.handleChange}
                label={formatMessage(messages.visibleColumnLabel)}
              />
            </Grid>
          </Grid>

          <Grid
            container
            size="auto"
            justifyContent="center"
            spacing={1}
            ml={1}
          >
            <LoadingButton
              variant="contained"
              onClick={handleSave}
              loading={isCreating || isUpdating}
            >
              {formatMessage(messages.saveButton)}
            </LoadingButton>

            <Button variant="outlined" color="primary" onClick={handleClose}>
              {formatMessage(messages.cancelButton)}
            </Button>
          </Grid>
        </Grid>
      </Slide>

      <Grid size={12} mt={3}>
        <Typography variant="h6">{formatMessage(messages.title)}</Typography>
      </Grid>

      <Grid size={12}>
        <DataTable
          columns={columns}
          enabledFooter={false}
          rows={tutorialQuestion.options}
        />
      </Grid>
    </Grid>
  );
}

function hideDiv(divRef: HTMLDivElement | null) {
  if (divRef) divRef.style.display = 'none';
}
